import { motion } from 'framer-motion';
import { FaBriefcase, FaGraduationCap, FaLaptop, FaUsers, FaUserTie, FaChartLine, FaLightbulb, FaCode, FaMobile, FaReact, FaServer, FaPalette, FaClockRotateLeft } from 'react-icons/fa6';

// Styles globaux pour l'animation de poussière
const globalStyles = `
  @keyframes dust-float {
    0%, 100% { 
      transform: translateY(-50%) translateX(0) scale(1); 
      opacity: 0.2; 
    }
    50% { 
      transform: translateY(-60%) translateX(5px) scale(1.2); 
      opacity: 0.6; 
    }
  }
`;

// Ajout des styles globaux
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

interface Experience {
  role: string;
  company: string;
  period: string;
  description: string;
}

interface Education {
  degree: string;
  school: string;
  period: string;
  description: string;
}

interface TimelineItem {
  type: 'experience' | 'education';
  title: string;
  subtitle: string;
  period: string;
  description: string;
  role?: string;
  company?: string;
}

const About = () => {
  const experience: Experience[] = [
    {
      role: 'Freelance',
      company: 'Raider Corporation',
      period: 'Mars - Juillet 2025',
      description: 'Création d\'une app e-commerce mobile pour l\'import de produits chinois. UI/UX optimisée et paiements sécurisés.'
    },
    {
      role: 'Freelance',
      company: 'Raider Corporation',
      period: 'Janvier - Mars 2025',
      description: 'Développement d\'applications web et mobiles pour divers secteurs (transit, coiffure, restauration).'
    },
    {
      role: 'Freelance',
      company: 'Collaboration',
      period: '2022 - 2024',
      description: 'Collaboration avec des équipes de création et développement sur l\'exécution d\'idées pour la création d\'une MarketPlace. Développement de solutions web innovantes et personnalisées pour divers clients.'
    },
    {
      role: 'Autodidacte',
      company: 'Bénevolat',
      period: '2021 - 2023',
      description: 'Acquisition de compétences en développement web de manière autonome à travers des ressources en ligne (tutoriels, forums, vidéos éducatives) et la pratique sur des projets personnels. Maîtrise de diverses technologies et méthodologies de développement web.'
    }
  ];

  const education: Education[] = [
    {
      degree: 'License 1 IT',
      school: 'U-Auben ISIG',
      period: '2022 - 2023',
      description: 'Début de Spécialisation en IT :\n- Développement et Génie Logiciel\n- Gestion des Données\n- Gestion Systèmes Informatiques'
    },
    {
      degree: 'BAC H (Informatique)',
      school: 'LTN/ASL',
      period: '2021 - 2022',
      description: 'Programmation et Développement de Logiciels, Gestion des Réseaux et Systèmes Informatiques, Bases de Données et Gestion des Données.'
    },
    {
      degree: 'BEPI',
      school: 'LTN/ASL',
      period: '2020 - 2021',
      description: 'Formation en informatique et programmation.'
    }
  ];

  // Configuration de l'animation pour la timeline
  const getItemAnimation = (index: number) => ({
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.15,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  });

  // Animation améliorée pour les points de la timeline
  const getDotAnimation = (index: number) => ({
    hidden: { 
      scale: 0,
      opacity: 0,
      y: 20
    },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 20,
        mass: 0.7,
        delay: 0.1 + (index * 0.08),
        restDelta: 0.001
      }
    },
    hover: {
      scale: 1.4,
      transition: { 
        type: 'spring',
        stiffness: 600,
        damping: 15,
        mass: 0.5
      }
    },
    tap: { 
      scale: 0.9 
    }
  });
  
  // Effet de vague amélioré
  const waveEffect = {
    initial: { 
      scale: 0.8, 
      opacity: 0.6 
    },
    animate: (i: number) => ({
      scale: 2.2,
      opacity: 0,
      transition: {
        duration: 2.5,
        delay: i * 0.05,
        repeat: Infinity,
        repeatType: 'loop',
        ease: [0.16, 1, 0.3, 1],
        repeatDelay: 0.5
      }
    })
  };
  
  // Effet de lueur au survol
  const glowEffect = {
    hover: { 
      opacity: 0.6, 
      scale: 2.2,
      transition: { 
        type: 'spring',
        stiffness: 400,
        damping: 15,
        mass: 0.5
      } 
    }
  };


  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
      <motion.div 
        className="bg-gray-900/50 backdrop-blur-sm p-5 sm:p-6 md:p-8 rounded-2xl border border-gray-700/50 shadow-2xl shadow-green-500/10 mb-8 sm:mb-10 md:mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2 
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-7 md:mb-8 bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent text-center md:text-left"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          À Propos de Moi
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <motion.div 
            className="md:col-span-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="text-green-400 mt-1">
                <FaUserTie className="text-2xl" />
              </div>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Salut ! Moi c'est Rani Hamed, un passionné de développement web et de création numérique.
                Webmaster dévoué, je m'épanouis dans la conception de sites web innovants qui allient esthétique et fonctionnalité.
              </p>
            </div>
            
            <div className="flex items-start gap-4 mb-6">
              <div className="text-green-400 mt-1">
                <FaChartLine className="text-2xl" />
              </div>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Mon parcours dans le monde numérique m'a permis d'acquérir une solide expertise dans la création d'expériences
                utilisateur exceptionnelles. Chaque projet est pour moi l'occasion de repousser les limites de la créativité
                tout en maintenant une approche centrée sur l'utilisateur.
              </p>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="text-green-400 mt-1">
                <FaLightbulb className="text-2xl" />
              </div>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Au-delà du développement, je m'intéresse aux dernières tendances technologiques, au design d'interface
                et à l'optimisation des performances. Je crois fermement en l'apprentissage continu et au partage des connaissances
                au sein de la communauté tech.
              </p>
            </div>
          </motion.div>
        
        <motion.div 
          className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 shadow-2xl shadow-green-500/10 h-fit"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">Expertises</h3>
          <ul className="space-y-4">
            <li className="flex items-start group">
              <div className="text-green-400 mt-1 mr-3 group-hover:scale-110 transition-transform">
                <FaCode className="text-xl" />
              </div>
              <span className="text-gray-300">Développement Web Full Stack</span>
            </li>
            <li className="flex items-start group">
              <div className="text-green-400 mt-1 mr-3 group-hover:scale-110 transition-transform">
                <FaMobile className="text-xl" />
              </div>
              <span className="text-gray-300">Applications Mobiles Cross-Platform (React Native)</span>
            </li>
            <li className="flex items-start group">
              <div className="text-green-400 mt-1 mr-3 group-hover:scale-110 transition-transform">
                <FaReact className="text-xl" />
              </div>
              <span className="text-gray-300">Frontend Moderne (React.js, TypeScript)</span>
            </li>
            <li className="flex items-start group">
              <div className="text-green-400 mt-1 mr-3 group-hover:scale-110 transition-transform">
                <FaServer className="text-xl" />
              </div>
              <span className="text-gray-300">Backend (Node.js, Express, bases de données)</span>
            </li>
            <li className="flex items-start group">
              <div className="text-green-400 mt-1 mr-3 group-hover:scale-110 transition-transform">
                <FaPalette className="text-xl" />
              </div>
              <span className="text-gray-300">UI/UX et Design d'Applications</span>
            </li>
          </ul>
        </motion.div>
      </div>
      </motion.div>

      <div className="mt-16 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="relative pb-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative">
              <div className="flex items-center justify-center space-x-3">
                <FaClockRotateLeft className="text-3xl text-green-400" />
                <h2 className="text-4xl font-bold text-white">
                  Parcours Professionnel et Académique
                </h2>
              </div>
              {/* Effet de poussière amélioré */}
              <div className="absolute -bottom-16 sm:-bottom-20 left-1/2 -translate-x-1/2 w-3/4 h-6 sm:h-8 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/20 to-transparent h-px top-1/2"></div>
                {[...Array(20)].map((_, i) => {
                  const size = Math.random() * 4 + 1;
                  const left = Math.random() * 100;
                  const duration = Math.random() * 3 + 2;
                  const delay = Math.random() * 2;
                  
                  return (
                    <div 
                      key={i}
                      className="absolute rounded-full bg-green-400/40"
                      style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        left: `${left}%`,
                        top: '50%',
                        opacity: Math.random() * 0.5 + 0.1,
                        animation: `dust-float ${duration}s ease-in-out ${delay}s infinite`,
                        transform: 'translateY(-50%)'
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </motion.div>
                <div className="relative py-8">
               {/* Ligne de temps droite pour mobile */}
               <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-green-400 to-blue-500 transform md:-translate-x-1/2"></div>
        
          {/* Conteneur des étapes */}
          <div className="relative space-y-10 sm:space-y-12 pl-8 sm:pl-12 md:pl-0">
            {/* Ligne de temps pour desktop */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-400 to-blue-500 -translate-x-1/2 z-0 hidden md:block"></div>
            {[
              ...experience.map(item => ({
                type: 'experience' as const,
                title: item.role,
                subtitle: item.company,
                period: item.period,
                description: item.description
              })),
              ...education.map(item => ({
                type: 'education' as const,
                title: item.degree,
                subtitle: item.school,
                period: item.period,
                description: item.description
              }))
            ]
              .sort((a, b) => {
                // Extraire l'année de début (première année dans la période)
                const yearA = parseInt(a.period.split(' - ')[0]);
                const yearB = parseInt(b.period.split(' - ')[0]);
                
                // Si une période est "Actuellement", on la met en premier
                if (a.period.includes('Actuellement')) return -1;
                if (b.period.includes('Actuellement')) return 1;
                
                // Tri décroissant (du plus récent au plus ancien)
                return yearB - yearA;
              })
              .map((item: TimelineItem, index) => {
                const isLeft = index % 2 === 0;

                return (
                  <motion.div 
                    key={index}
                    className={`relative flex flex-col md:flex-row items-start md:items-center mb-12 sm:mb-14 md:mb-16 ml-0 md:ml-0 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-100px 0px -100px 0px' }}
                    variants={getItemAnimation(index)}
                    style={{ willChange: 'opacity, transform' }}
                  >
                    {/* Point sur la timeline avec effet de vague */}
                    <div className="absolute left-0 md:left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                      <motion.div 
                        className="relative w-10 h-10 flex items-center justify-center cursor-pointer"
                        whileHover="hover"
                        whileTap="tap"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                        variants={getDotAnimation(index)}
                      >
                        {/* Effet de vague amélioré */}
                        <motion.span
                          className={`absolute w-4 h-4 rounded-full ${item.type === 'experience' ? 'bg-green-500/40' : 'bg-blue-500/40'}`}
                          custom={index}
                          variants={waveEffect}
                          initial="initial"
                          animate="animate"
                        />
                        {/* Point principal */}
                        <motion.div 
                          className={`w-4 h-4 rounded-full ${item.type === 'experience' ? 'bg-green-500' : 'bg-blue-500'} border-2 border-white/20 shadow-lg z-10`}
                        />
                        {/* Effet de lueur au survol */}
                        <motion.div 
                          className={`absolute inset-0 rounded-full ${item.type === 'experience' ? 'bg-green-500' : 'bg-blue-500'} opacity-0`}
                          style={{ filter: 'blur(8px)' }}
                          variants={glowEffect}
                        />
                      </motion.div>
                    </div>
                    
                    {/* Carte de contenu */}
                    <div className={`w-full md:w-5/12 ${!isLeft ? 'md:mr-6' : 'md:ml-6'} mt-8 md:mt-0`}>
                      <motion.div 
                        className={`bg-gray-900/60 backdrop-blur-sm p-4 sm:p-5 md:p-6 rounded-xl border-l-4 ${item.type === 'experience' ? 'border-green-500' : 'border-blue-500'} hover:shadow-lg transition-all duration-300 hover:border-opacity-100 border-opacity-70 shadow-lg ${item.type === 'experience' ? 'shadow-green-500/10' : 'shadow-blue-500/10'}`}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ 
                          opacity: 1, 
                          x: 0,
                          transition: {
                            duration: 0.6,
                            delay: 0.1 + (index * 0.1),
                            ease: [0.16, 1, 0.3, 1]
                          }
                        }}
                        viewport={{ once: false, margin: '-50px 0px -50px 0px' }}
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                          <div>
                            <h4 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                              {item.type === 'experience' ? (
                              item.role === 'Freelance' ? (
                                <FaLaptop className="text-green-400" />
                              ) : item.role === 'Collaboration' ? (
                                <FaUsers className="text-green-400" />
                              ) : (
                                <FaBriefcase className="text-green-400" />
                              )
                            ) : (
                              <FaGraduationCap className="text-blue-400" />
                            )}
                              {item.title}
                            </h4>
                            <p className={`text-sm font-medium ${item.type === 'experience' ? 'text-green-400' : 'text-blue-400'}`}>
                              {item.subtitle}
                            </p>
                          </div>
                          <span className="text-sm text-gray-400 whitespace-nowrap bg-gray-800/50 px-2 py-1 rounded-full border border-gray-700/50">
                            {item.period}
                          </span>
                        </div>
                        <p className="mt-3 text-gray-300 text-sm leading-relaxed">
                          {item.description.split('\n').map((line, i) => (
                            <span key={i} className="block">{line}</span>
                          ))}
                        </p>
                        <div className={`mt-3 text-xs font-mono px-2 sm:px-3 py-1 sm:py-1.5 rounded-full inline-flex items-center gap-1 sm:gap-2 ${
                          item.type === 'experience' 
                            ? 'text-green-400 bg-green-900/30 border border-green-800/30'
                            : 'text-blue-400 bg-blue-900/30 border border-blue-800/30'
                        }`}>
                          {item.type === 'experience' ? (
                            <>
                              <FaBriefcase className="text-xs" />
                              <span>Expérience Professionnelle</span>
                            </>
                          ) : (
                            <>
                              <FaGraduationCap className="text-xs" />
                              <span>Formation</span>
                            </>
                          )}
                        </div>
                      </motion.div>
                    </div>
                    
                  </motion.div>
                );
              })}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default About;
